import axios from 'axios';

import { RIBI_COUNTRY_LIST } from '@domain/common';

import { log } from '@hooks/logger';

import { DIS } from '@typings/dis';
import { LogLevel } from '@typings/operations';

export const getDistrictViewInvoiceReport = async (clubId: string) => {
  // Parse query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const docType = urlParams.get('docType') || 'I';
  const isCurrentInvoice = urlParams.get('isCurrentInvoice') === 'true';

  const fetchClubInvoiceDataQuery = `
  query fetchClubInvoiceData($clubId: String!) {
    club: clubById(clubId: $clubId) {
      clubType
      riClubId
      district
      physicalLocation {       
        country
      }
    }
  }
  `;

  const fetchDistrictFinanceAccessQuery = `
  query fetchDistrictFinanceAccess($districtId: String!) {
    districtFinanceOperationAccess(districtId: $districtId)
  }
  `;

  const graphqlQuery = `
  query fetchClubInvoiceLink($clubId: Int!, $clubType: Int!, $docType: String!, $isCurrentInvoice: Boolean!) {
    getClubInvoiceReport(clubId: $clubId, clubType: $clubType, docType: $docType, isCurrentInvoice: $isCurrentInvoice) {
      fileContent
    }
  }
  `;

  const graphqlEndpoint = `${process.env.GATSBY_MRX_API_BUILD_URL}/graphql`;

  const commonHeaders = {
    'Content-Type': 'application/json',
  };

  const channel = new BroadcastChannel('district-invoice-reports');
  const triggerErrorNotification = () => {
    setTimeout(() => {
      channel.postMessage({ type: 'error' });
    }, 5000);
  };
  try {
    // Fetch club invoice data
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: fetchClubInvoiceDataQuery,
        variables: { clubId },
      },
      {
        headers: commonHeaders,
        withCredentials: true,
      }
    );

    const data = response?.data?.data?.club;
    const districtId = data?.district;
    const clubCountry = data?.physicalLocation?.country || '';
    const isRibiCountry =
      RIBI_COUNTRY_LIST.includes(clubCountry) &&
      data.clubType === DIS.ClubTypeEnum.RotaryClub;

    let clubType;
    if (data?.clubType === 'Rotary_Club') {
      clubType = 3;
    } else if (data?.clubType === 'Rotaract_Club') {
      clubType = 4;
    }

    let hasFinanceAccess = false;

    if (districtId) {
      try {
        const districtResponse = await axios.post(
          graphqlEndpoint,
          {
            query: fetchDistrictFinanceAccessQuery,
            variables: { districtId },
          },
          {
            headers: commonHeaders,
            withCredentials: true,
          }
        );

        hasFinanceAccess =
          districtResponse?.data?.data?.districtFinanceOperationAccess ?? false;
      } catch (error) {
        triggerErrorNotification();
        if (axios.isAxiosError(error)) {
          log({
            level: LogLevel.Debug,
            message: `Gatsby: fetchDistrictFinanceAccess, 'GraphQL Request Failed:', ${error.message}`,
          });
        } else {
          log({
            level: LogLevel.Debug,
            message: `Gatsby: fetchDistrictFinanceAccess, 'Unknown error occurred:', ${error}`,
          });
        }
      }
    }

    if (!isRibiCountry && hasFinanceAccess) {
      const pdfVariables = {
        clubType,
        clubId: Number(data?.riClubId),
        docType,
        isCurrentInvoice,
      };

      try {
        const pdfResponse = await axios.post(
          graphqlEndpoint,
          {
            query: graphqlQuery,
            variables: pdfVariables,
          },
          {
            headers: commonHeaders,
            withCredentials: true,
          }
        );

        const pdfData = pdfResponse?.data?.data?.getClubInvoiceReport;

        if (pdfData && typeof pdfData === 'object' && pdfData.fileContent) {
          const pdfWindow = window.open('', '_blank');
          if (pdfWindow) {
            pdfWindow.document.write(
              `<iframe width="100%" height="100%" src="data:application/pdf;base64,${encodeURIComponent(
                pdfData.fileContent ?? ''
              )}"></iframe>`
            );
          }
        } else {
          triggerErrorNotification();
        }
      } catch (error) {
        triggerErrorNotification();
        if (axios.isAxiosError(error)) {
          log({
            level: LogLevel.Debug,
            message: `Gatsby: getDistrictViewInvoiceReport, 'GraphQL Request Failed:', ${error.message}`,
          });
        } else {
          log({
            level: LogLevel.Debug,
            message: `Gatsby: getDistrictViewInvoiceReport, 'Unknown error occurred:', ${error}`,
          });
        }
      }
    }
  } catch (error) {
    triggerErrorNotification();
    if (axios.isAxiosError(error)) {
      log({
        level: LogLevel.Debug,
        message: `Gatsby: fetchClubInvoiceData, 'GraphQL Request Failed:', ${error.message}`,
      });
    } else {
      log({
        level: LogLevel.Debug,
        message: `Gatsby: fetchClubInvoiceData, 'Unknown error occurred:', ${error}`,
      });
    }
  }
};
